/**
 * Responsive invoice line items: a table on desktop and readable cards on
 * narrow screens, with the same financial fields and actions in each view.
 */
import { formatCurrency, formatNumber } from '@armard/shared';
import { unitTypeLabels, type InvoiceItem } from '../../hooks/useInvoices';
import { InvoiceItemRow } from './InvoiceItemRow';

type InvoiceItemView = Pick<
  InvoiceItem,
  | 'description'
  | 'quantity'
  | 'unitType'
  | 'unitPrice'
  | 'discountType'
  | 'discountValue'
  | 'lineTotal'
> & { id?: string; tempId?: string };

interface InvoiceItemsProps {
  items: InvoiceItemView[];
  onEdit?: (index: number) => void;
  onDelete?: (index: number) => void;
  readOnly?: boolean;
}

export function InvoiceItems({ items, onEdit, onDelete, readOnly = false }: InvoiceItemsProps) {
  const hasActions = !readOnly && (onEdit || onDelete);

  return (
    <>
      <div className="hidden md:block">
        <table className="w-full">
          <thead>
            <tr className="text-left text-sm text-white/50 border-b border-white/10">
              <th className="pb-2 px-2 w-12 text-center">#</th>
              <th className="pb-2 px-3">Beschreibung</th>
              <th className="pb-2 px-3 text-right">Menge</th>
              <th className="pb-2 px-3 text-right">Preis</th>
              <th className="pb-2 px-3 text-right">Gesamt</th>
              {hasActions && <th className="pb-2 px-2 w-24" />}
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <InvoiceItemRow
                key={item.id ?? item.tempId ?? index}
                item={item as InvoiceItem}
                position={index + 1}
                onEdit={onEdit ? () => onEdit(index) : undefined}
                onDelete={onDelete ? () => onDelete(index) : undefined}
                readOnly={readOnly}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-3">
        {items.map((item, index) => {
          const quantity = Number(item.quantity);
          const hasDiscount = item.discountType && Number(item.discountValue ?? 0) > 0;
          const discountLabel =
            item.discountType === 'percent'
              ? `−${formatNumber(Number(item.discountValue), Number.isInteger(Number(item.discountValue)) ? 0 : 2)}%`
              : item.discountType === 'absolute'
                ? `−${formatCurrency(Number(item.discountValue))}`
                : null;

          return (
            <article
              key={item.id ?? item.tempId ?? index}
              className="rounded-xl border border-white/10 bg-white/[0.02] p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs text-white/40">Position {index + 1}</div>
                  <div className="mt-1 text-white font-medium break-words">{item.description}</div>
                </div>
                {hasActions && (
                  <div className="flex shrink-0 items-center gap-1 -mr-1 -mt-1">
                    {onEdit && (
                      <button
                        type="button"
                        onClick={() => onEdit(index)}
                        className="min-h-11 min-w-11 p-2 text-white/50 hover:text-white hover:bg-white/5 rounded transition-colors"
                        aria-label={`Position ${index + 1} bearbeiten`}
                        title="Bearbeiten"
                      >
                        <svg
                          className="w-5 h-5 mx-auto"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </button>
                    )}
                    {onDelete && (
                      <button
                        type="button"
                        onClick={() => onDelete(index)}
                        className="min-h-11 min-w-11 p-2 text-white/50 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                        aria-label={`Position ${index + 1} entfernen`}
                        title="Löschen"
                      >
                        <svg
                          className="w-5 h-5 mx-auto"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                )}
              </div>

              <dl className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-white/45">Menge</dt>
                  <dd className="text-white text-right">
                    {item.unitType === 'flat_rate' && quantity === 1
                      ? '—'
                      : `${formatNumber(quantity)} ${unitTypeLabels[item.unitType]}`}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-white/45">Einzelpreis</dt>
                  <dd className="text-white text-right">
                    {formatCurrency(Number(item.unitPrice))}
                  </dd>
                </div>
                {hasDiscount && discountLabel && (
                  <div className="flex justify-between gap-3">
                    <dt className="text-orange-400/80">Positionsrabatt</dt>
                    <dd className="text-orange-400 text-right">{discountLabel}</dd>
                  </div>
                )}
                <div className="flex justify-between gap-3 border-t border-white/10 pt-2">
                  <dt className="text-white/65 font-medium">Gesamt</dt>
                  <dd className="text-white font-semibold text-right">
                    {formatCurrency(Number(item.lineTotal))}
                  </dd>
                </div>
              </dl>
            </article>
          );
        })}
      </div>
    </>
  );
}
